import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { apiRequest } from "../../api/api";
import UsPhoneField, { toPhoneDigits, toE164 } from "../shared/UsPhoneField";

const GuestInfo = ({ guestInfo, setGuestInfo }) => {
  const [emailStatus, setEmailStatus] = useState(null); // null | "checking" | "real_account" | "ghost" | "new"
  const [emailChecked, setEmailChecked] = useState("");

  const handleChange = (field) => (e) => {
    setGuestInfo((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleEmailBlur = async () => {
    const email = guestInfo.email?.trim().toLowerCase();
    if (!email || email === emailChecked) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    setEmailChecked(email);
    setEmailStatus("checking");

    try {
      const res = await apiRequest(`/guests/check-email.php?email=${encodeURIComponent(email)}`);
      const { exists, is_guest } = res.data;
      if (!exists) {
        setEmailStatus("new");
      } else if (is_guest) {
        setEmailStatus("ghost");
      } else {
        setEmailStatus("real_account");
      }
    } catch {
      setEmailStatus(null);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      fontSize: "0.9rem",
      backgroundColor: "offWhite",
      "& fieldset": { borderColor: "#d8d1c7" },
      "&:hover fieldset": { borderColor: "#b0a898" },
      "&.Mui-focused fieldset": { borderColor: "#111008" },
    },
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField
            label="First name"
            fullWidth
            value={guestInfo.firstName}
            onChange={handleChange("firstName")}
            sx={inputSx}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label="Last name"
            fullWidth
            value={guestInfo.lastName}
            onChange={handleChange("lastName")}
            sx={inputSx}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={guestInfo.email}
            onChange={handleChange("email")}
            onBlur={handleEmailBlur}
            sx={inputSx}
            InputProps={{
              endAdornment: emailStatus === "checking" ? (
                <CircularProgress size={16} sx={{ color: "text.secondary" }} />
              ) : null,
            }}
          />
          {emailStatus === "real_account" && (
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon fontSize="small" />}
              sx={{
                mt: 1,
                fontSize: "0.82rem",
                backgroundColor: "rgba(196,152,77,0.08)",
                color: "#111",
                border: "1px solid rgba(196,152,77,0.2)",
                "& .MuiAlert-icon": { color: "#C4984D" },
              }}
            >
              Looks like you already have an account.{" "}
              <strong
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => window.location.href = "/login"}
              >
                Log in
              </strong>{" "}
              for a faster experience — or continue as a guest below.
            </Alert>
          )}
          {emailStatus === "ghost" && (
            <Alert
              severity="success"
              icon={<InfoOutlinedIcon fontSize="small" />}
              sx={{
                mt: 1,
                fontSize: "0.82rem",
                backgroundColor: "rgba(196,152,77,0.08)",
                color: "#111",
                border: "1px solid rgba(196,152,77,0.2)",
                "& .MuiAlert-icon": { color: "#C4984D" },
              }}
            >
              Welcome back! We found your previous booking info — your details have been pre-filled.
            </Alert>
          )}
        </Grid>
        <Grid size={12}>
          <UsPhoneField
            value={toPhoneDigits(guestInfo.phone)}
            onChange={(digits) =>
              setGuestInfo((prev) => ({ ...prev, phone: toE164(digits) }))
            }
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: "10px",
          backgroundColor: "#fafaf8",
          border: "1px solid #e8e2d8",
        }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.82rem", lineHeight: 1.6 }}>
          Your contact info is used to send your booking confirmation and magic
          link — which lets you cancel or reschedule without needing an account.
          We never share your information.
        </Typography>
      </Box>
    </Box>
  );
};

export default GuestInfo;
